import React from 'react';
import PropTypes from 'prop-types';

import {Box, Button, Drawer, Divider, IconButton, Stack, TextField, Typography} from 'views/components';
import {Close} from 'views/icons';
import {Translate, translate} from 'views/module';

import {FEEDBACK_OPTIONS} from './utils/constants';

const CaregiverFeedbackDrawer = (props) => {
  const {
    isMatch,
    caregiverFirstName,
    selectedOptions,
    setSelectedOptions,
    additionalComments,
    setAdditionalComments,
    isFeedbackDrawerOpen,
    setIsFeedbackDrawerOpen,
    handleSubmitFeedback,
    isFeedbackSending,
  } = props;

  const handleClick = (isSelected, topic) => {
    if (isSelected) {
      setSelectedOptions(selectedOptions.filter((topicResponse) => topicResponse !== topic));
    } else {
      setSelectedOptions([...selectedOptions, topic]);
    }
  };

  return (
    <Drawer
      anchor="bottom"
      open={isFeedbackDrawerOpen}
      onOpen={() => setIsFeedbackDrawerOpen(true)}
      onClose={() => setIsFeedbackDrawerOpen(false)}
      slotProps={{
        paper: {
          sx: {
            width: '100%',
            py: 2,
            height: {xs: '75vh', sm: 'auto'},
            maxHeight: {xs: '75vh', sm: '80%'},
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px 16px 0 0',
            overflow: 'hidden',
            bgcolor: '#FFFDFC',
            border: '1px solid',
            borderColor: 'color.divider',
          },
        },
      }}
      disableSwipeToOpen
      keepMounted
    >
      <Stack
        spacing={2}
        alignItems="center"
        sx={{
          position: 'relative',
          width: '100%',
          px: 4,
          pb: 2,
        }}
      >
        <IconButton
          size="large"
          onClick={() => setIsFeedbackDrawerOpen(false)}
          sx={{
            'position': 'absolute',
            'top': {xs: 0, sm: 0},
            'right': {xs: 16, sm: 16},
            'color': 'grey.600',
            'bgcolor': '#F2EFE9',
            '&:hover': {bgcolor: '#E9E3DC'},
          }}
        >
          <Close fontSize="small" />
        </IconButton>
        {/* Titles */}
        <Stack alignSelf="flex-start" spacing={{xs: 1, md: 2}}>
          <Typography
            variant="h6"
            fontWeight={600}
            lineHeight={'140%'}
          >
            <Translate
              expression={`caregiverDetail.CaregiverFeedback.DrawerTitle.${isMatch ? 'IsAMatch' : 'IsNotAMatch'}`}
              substitute={{caregiverName: caregiverFirstName}}
            />
          </Typography>
          <Typography
            variant="subtitle2"
            color="#686868"
            fontWeight={500}
          >
            <Translate
              expression={`caregiverDetail.CaregiverFeedback.DrawerSubtitle.${isMatch ? 'IsAMatch' : 'IsNotAMatch'}`}
            />
          </Typography>
        </Stack>
        <Stack sx={{width: '100%', mx: -4}}>
          <Divider sx={{mx: -4}}/>
        </Stack>
      </Stack>
      <Stack
        spacing={2}
        alignItems="center"
        sx={{
          flex: 1,
          width: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
          px: 4,
          pb: 2,
        }}
      >
        {/* Feedback Options & Additional Comments */}
        <Stack
          sx={{
            width: {
              xs: '100%',
              sm: '75%',
              md: '50%',
            },
            py: 1,
          }}
        >
          <Stack
            spacing={1}
          >
            <Typography
              variant="subtitle1"
              fontWeight={500}
            >
              <Translate expression='caregiverDetail.CaregiverFeedback.FeedbackOptionsTitle'/>
            </Typography>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  sm: 'repeat(2, minmax(0, 1fr))',
                },
                rowGap: 1,
                columnGap: 2,
              }}
            >
              {
                FEEDBACK_OPTIONS.map((topic) => {
                  const isSelected = selectedOptions.includes(topic.value);
                  return (
                    <Button
                      key={topic.value}
                      variant={isSelected ? 'contained' : 'outlined'}
                      disableElevation
                      sx={{
                        'py': 2,
                        'px': 4,
                        'borderRadius': 3,
                        'borderStyle': 'solid',
                        'borderColor': isSelected ? (isMatch ? '#68A873' : 'primary.main') : 'grey.400',
                        'borderWidth': 2,
                        'color': 'color.black',
                        'bgcolor': isSelected ? (isMatch ? '#F4FBF7' : '#FFF7EC'): 'color.white',
                        '&:hover': {
                          borderColor: isSelected ? (isMatch ? '#68A873' : 'primary.main') : 'grey.500',
                        },
                      }}
                      onClick={() => handleClick(isSelected, topic.value)}
                    >
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 400,
                          textTransform: 'none',
                        }}
                      >
                        <Translate
                          expression={topic.translationKey}
                          substitute={{caregiverName: caregiverFirstName}}
                        />
                      </Typography>
                    </Button>
                  );
                })
              }
            </Box>
          </Stack>
          <Stack
            spacing={1}
            sx={{
              mt: 2,
            }}
          >
            <Typography
              variant="subtitle1"
              fontWeight={500}
            >
              <Translate expression='eventFeedback.CanWeImprove.AdditionalCommentsSubtitle'/>
            </Typography>
            <TextField
              value={additionalComments}
              variant="outlined"
              onChange={(newValue) => {
                setAdditionalComments(newValue);
              }}
              placeholder={translate('eventFeedback.CanWeImprove.AdditionalCommentsPlaceholder')}
              rows={4}
              multiline
              fullWidth
              sx={{
                '& .MuiOutlinedInput-notchedOutline': {
                  borderWidth: '1.5px',
                },
                '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'color.black',
                },
                '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'color.black',
                },
                '& .MuiOutlinedInput-root.Mui-focused': {
                  boxShadow: 'color.black',
                },
              }}
            />
          </Stack>
        </Stack>
      </Stack>
      {/* Cancel & Submit Buttons */}
      <Stack
        direction="row"
        spacing={2}
        width="100%"
        sx={{
          pt: 2,
          px: 2,
          pb: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          bgcolor: '#FFFDFC',
          borderTop: '2px solid',
          borderColor: 'primary.main',
        }}
      >
        {/* Cancel Button */}
        <Button
          variant={'outlined'}
          sx={{
            'width': '100%',
            'py': 1.75,
            'px': {xs: 1, md: 4},
            'borderRadius': '9px',
            'color': 'color.black',
            'bgcolor': 'color.white',
            '&:hover': {
              bgcolor: '#F2EFE9',
            },
          }}
          onClick={() => {
            setIsFeedbackDrawerOpen(false);
          }}
          disabled={isFeedbackSending}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              textTransform: 'none',
            }}
            fontSize={{xs: '14px', md: '16px'}}
          >
            <Translate expression="caregiverDetail.CaregiverFeedback.Cancel" />
          </Typography>
        </Button>
        {/* Submit Button */}
        <Button
          variant={'contained'}
          sx={{
            'width': '100%',
            'py': 1.75,
            'px': {xs: 1, md: 4},
            'borderRadius': '9px',
            'color': 'color.white',
            'bgcolor': isMatch ? '#68A873' : 'primary.main',
            '&:hover': {
              bgcolor: isMatch ? '#5E9B6B' : 'primary.dark',
            },
          }}
          onClick={() => {
            handleSubmitFeedback();
          }}
          disabled={isFeedbackSending}
        >
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: 500,
              textTransform: 'none',
            }}
            fontSize={{xs: '14px', md: '16px'}}
          >
            <Translate expression={`caregiverDetail.CaregiverFeedback.Submit.${isMatch ? 'IsAMatch' : 'IsNotAMatch'}`}/>
          </Typography>
        </Button>
      </Stack>
    </Drawer>
  );
};

CaregiverFeedbackDrawer.propTypes = {
  isMatch: PropTypes.bool.isRequired,
  caregiverFirstName: PropTypes.string.isRequired,
  selectedOptions: PropTypes.array.isRequired,
  setSelectedOptions: PropTypes.func.isRequired,
  additionalComments: PropTypes.string.isRequired,
  setAdditionalComments: PropTypes.func.isRequired,
  isFeedbackDrawerOpen: PropTypes.bool.isRequired,
  isFeedbackSending: PropTypes.bool.isRequired,
  setIsFeedbackDrawerOpen: PropTypes.func.isRequired,
  handleSubmitFeedback: PropTypes.func.isRequired,
};

export default React.memo(CaregiverFeedbackDrawer);
